import { memo, useMemo, useReducer } from 'react';

import { Download, SquarePen, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import { IRaffleForm } from '../form';
import { RaffleFormDialog } from '../raffleFormDialog/RaffleFormDialog';

import css from './RaffleActionsColumn.module.css';

import { referralRaffleService } from 'v2/api/referralRaffleService';
import { Popconfirm } from 'v2/components/popconfirm/Popconfirm';
import { ReferralRaffle } from 'v2/types/ReferralRaffleModel';
import { handleHttpError } from 'v2/utils';

interface RaffleActionsColumnProps {
  raffle: ReferralRaffle;
  onRevalidate: VoidFunction;
}

export const RaffleActionsColumn = memo<RaffleActionsColumnProps>(({ raffle, onRevalidate }) => {
  const [isEditOpen, setEditOpen] = useReducer((state) => !state, false);

  const { trigger: triggerToggleActive } = useSWRMutation(
    ['toggleRaffleActive', raffle.id],
    referralRaffleService.toggleRaffleActive,
    {
      onSuccess: () => {
        onRevalidate();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerUpdate, isMutating } = useSWRMutation(
    ['updateRaffle', raffle.id],
    referralRaffleService.updateRaffle,
    {
      onSuccess: () => {
        onRevalidate();
        toast.success('The raffle has been successfully updated');
        setEditOpen();
      },
      onError: handleHttpError,
    }
  );

  const { trigger: triggerDelete } = useSWRMutation(['deleteRaffle', raffle.id], referralRaffleService.deleteRaffle, {
    onSuccess: () => {
      onRevalidate();
      toast.success('The raffle has been successfully deleted');
    },
    onError: handleHttpError,
  });

  const { trigger: triggerExport, isMutating: isExporting } = useSWRMutation(
    ['exportRaffleParticipants', raffle.id],
    referralRaffleService.exportRaffleParticipants,
    {
      onError: handleHttpError,
    }
  );

  const editInitialValues: IRaffleForm = useMemo(
    () => ({
      name: raffle.name,
      participantsMaxCount: raffle.participantsMaxCount,
      rewardValue: raffle.reward.value ?? '',
      rewardImageId: raffle.reward.imageId ?? '',
    }),
    [raffle]
  );

  const handleEditSubmit = (values: IRaffleForm, resetForm: VoidFunction) => {
    triggerUpdate({
      name: values.name,
      participantsMaxCount: values.participantsMaxCount,
      reward: {
        value: values.rewardValue || undefined,
        imageId: values.rewardImageId,
      },
    }).then(() => {
      resetForm();
    });
  };

  return (
    <div className={css.raffleActionsColumn}>
      <Checkbox color="success" checked={raffle.isActive} onChange={() => triggerToggleActive()} />

      <Tooltip title="Export Participants" placement="top">
        <IconButton onClick={() => triggerExport()} disabled={isExporting} color="primary">
          {isExporting ? <CircularProgress size={22} /> : <Download width={22} height={22} />}
        </IconButton>
      </Tooltip>

      <Tooltip title="Edit" placement="top">
        <IconButton onClick={setEditOpen} color="warning">
          <SquarePen width={22} height={22} />
        </IconButton>
      </Tooltip>

      <Popconfirm
        message="Are you sure? All participants of this raffle will be deleted as well."
        onConfirm={triggerDelete}
      >
        <Tooltip title="Delete" placement="top">
          <IconButton color="error">
            <Trash2 width={22} height={22} />
          </IconButton>
        </Tooltip>
      </Popconfirm>

      <RaffleFormDialog
        title="Edit Raffle"
        isOpen={isEditOpen}
        onClose={setEditOpen}
        initialValues={editInitialValues}
        isSubmitting={isMutating}
        onSubmit={handleEditSubmit}
      />
    </div>
  );
});

RaffleActionsColumn.displayName = 'RaffleActionsColumn';
